/**
 * useDashboardData - データ取得の統一フック
 * アクション種別に応じて適切なAPIを呼び出し、データを取得する
 */
import { useEffect, useState, useRef } from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import { useAdminPreviewDataOverride } from '../contexts/AdminPreviewDataContext';
import { actionAllowsPrediction, getActionConfig, needsLocation } from '../config/actions';
import * as graphApi from '../api/graphApi';
import * as congestionApi from '../api/congestionApi';
import * as foreignersApi from '../api/foreignersApi';
import { detectLanguageFromPathname, translate } from '../i18n';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

const PREDICTION_VALUE_KEYS = ['congestion', 'congestion_level', 'value', 'count', 'level'];

const stripPredictionSource = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripPredictionSource);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }

  const source = value.source;
  const next = {};
  Object.entries(value).forEach(([key, nested]) => {
    if (key === 'source' && (nested === 'prediction' || nested === 'mixed')) {
      next[key] = 'actual';
      return;
    }
    next[key] = stripPredictionSource(nested);
  });

  if (source === 'prediction' || source === 'mixed') {
    PREDICTION_VALUE_KEYS.forEach((key) => {
      if (key in next) next[key] = null;
    });
  }
  return next;
};

const sanitizePredictionData = (result, allowPrediction) => {
  if (allowPrediction) {
    return result;
  }
  return {
    ...result,
    predictionMeta: null,
    data: stripPredictionSource(result.data),
    calendarData: stripPredictionSource(result.calendarData),
    previousMonthCalendarData: stripPredictionSource(result.previousMonthCalendarData),
  };
};

/**
 * ダッシュボードデータ取得フック
 * @returns {Object} データ取得状態とデータ
 */
export function useDashboardData() {
  const flags = useFeatureFlags();
  const adminOverride = useAdminPreviewDataOverride();
  const { location, action, year, month } = useDashboard();
  const [state, setState] = useState({
    data: null,
    calendarData: [],
    aiAdvice: null,
    weatherData: [],
    eventData: [],
    previousMonthCalendarData: [],
    previousMonthWeatherData: [],
    previousMonthEventData: [],
    previousYear: null,
    previousMonth: null,
    predictionMeta: null,
    responseType: null,
    loading: false,
    error: null,
  });
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (adminOverride != null) {
      setState(adminOverride);
    }
  }, [adminOverride]);

  useEffect(() => {
    if (adminOverride != null) {
      return undefined;
    }

    // アクション設定を取得
    const actionConfig = getActionConfig(action);
    
    // 選択が不完全な場合は何もしない
    const locationRequired = needsLocation(action);
    if (!action || !year || !month || (locationRequired && !location)) {
      // 不完全な選択になった場合は前のデータも見えないようリセットする
      setState({
        data: null,
        calendarData: [],
        aiAdvice: null,
        weatherData: [],
        eventData: [],
        previousMonthCalendarData: [],
        previousMonthWeatherData: [],
        previousMonthEventData: [],
        previousYear: null,
        previousMonth: null,
        predictionMeta: null,
        responseType: null,
        loading: false,
        error: null,
      });
      return;
    }
    
    // 前回のリクエストをキャンセル
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // アクション/場所/年月が切り替わった瞬間に前のグラフが残らないよう
    // データを即時クリアし、loading 状態のみを表示する
    setState({
      data: null,
      calendarData: [],
      aiAdvice: null,
      weatherData: [],
      eventData: [],
      previousMonthCalendarData: [],
      previousMonthWeatherData: [],
      previousMonthEventData: [],
      previousYear: null,
      previousMonth: null,
      predictionMeta: null,
      responseType: null,
      loading: true,
      error: null,
    });
    
    // アクションの種別に応じてAPI呼び分け
    const fetchData = async () => {
      if (actionConfig.apiType === 'congestion') {
        // today_details の場合
        const data = await congestionApi.getCongestionDetail(location, { signal });
        return { 
          data, 
          calendarData: [],
          aiAdvice: null, 
          weatherData: [], 
          eventData: [],
          previousMonthCalendarData: [],
          previousMonthWeatherData: [],
          previousMonthEventData: [],
          previousYear: null,
          previousMonth: null,
          predictionMeta: null,
          responseType: null,
        };
      }

      if (actionConfig.apiType === 'foreigners') {
        // foreigners_* の場合
        const params = action === 'foreigners_distribution' 
          ? { month: parseInt(month), year, top_n: 5 }
          : { year, top_n: 50 };
        const apiMethod = action === 'foreigners_distribution'
          ? foreignersApi.getMonthlyRanking
          : foreignersApi.getYearlyDistribution;
        const response = await apiMethod(params, signal);
        return { 
          data: response.data,
          calendarData: [],
          aiAdvice: null, 
          weatherData: [], 
          eventData: [],
          previousMonthCalendarData: [],
          previousMonthWeatherData: [],
          previousMonthEventData: [],
          previousYear: null,
          previousMonth: null,
          predictionMeta: null,
          responseType: null,
        };
      }
      
      // その他は統一API (graph)
      const response = await graphApi.getGraph({
        place: location,
        action,
        year: parseInt(year),
        month: parseInt(month),
      }, signal);
      
      // event_effect の場合はスキップ（コンポーネント内で独自に処理）
      if (action === 'event_effect') {
        return {
          data: null,
          calendarData: [],
          aiAdvice: response.ai_advice || null,
          weatherData: response.weather_data || [],
          eventData: response.event_data || [],
          previousMonthCalendarData: [],
          previousMonthWeatherData: [],
          previousMonthEventData: [],
          previousYear: null,
          previousMonth: null,
          predictionMeta: null,
          responseType: null,
        };
      }
      
      // 傾向分析データかどうかを判定
      const isTrendAnalysis = ['year_trend', 'month_trend', 'week_trend'].includes(action);
      
      if (isTrendAnalysis) {
        // 傾向分析の場合
        return {
          data: response.data,
          calendarData: [],
          aiAdvice: response.ai_advice || null,
          weatherData: response.weather_data || [],
          eventData: response.event_data || [],
          previousMonthCalendarData: [],
          previousMonthWeatherData: [],
          previousMonthEventData: [],
          previousYear: null,
          previousMonth: null,
          predictionMeta: response.prediction_meta || null,
          responseType: action,
        };
      }
      
      // カレンダー・ヒートマップなどの通常データ
      return {
        data: null,
        calendarData: response.data || [],
        aiAdvice: response.ai_advice || null,
        weatherData: response.weather_data || [],
        eventData: response.event_data || [],
        previousMonthCalendarData: response.previous_month_data || [],
        previousMonthWeatherData: response.previous_month_weather_data || [],
        previousMonthEventData: response.previous_month_event_data || [],
        previousYear: response.previous_year || null,
        previousMonth: response.previous_month || null,
        predictionMeta: response.prediction_meta || null,
        responseType: null,
      };
    };

    fetchData()
      .then((result) => setState({
        ...sanitizePredictionData(result, flags.prediction && actionAllowsPrediction(action)),
        loading: false,
        error: null,
      }))
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Error fetching data:', err);
          const language = detectLanguageFromPathname(window.location.pathname);
          setState(prev => ({
            ...prev,
            loading: false,
            error: translate(language, 'common.fetchError'),
          }));
        }
      });

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [adminOverride, location, action, year, month, flags.prediction]);

  return adminOverride != null ? adminOverride : state;
}
